<template>
  <div class="ski-school-detail-page">
    <!-- Loading Spinner -->
    <LoadingSpinner v-if="loading" />

    <!-- Error Message -->
    <div v-else-if="error" class="error-message">
      <div class="error-content">
        <i class="fas fa-exclamation-triangle"></i>
        <h3>Ski School Not Found</h3>
        <p>{{ error }}</p>
        <router-link to="/ski-schools" class="back-button">
          <i class="fas fa-arrow-left"></i>
          Back to Ski Schools
        </router-link>
      </div>
    </div>

    <!-- Ski School Detail -->
    <div v-else-if="skiSchool" class="ski-school-detail">
      <!-- Back Button -->
      <div class="navigation-header">
        <router-link to="/ski-schools" class="back-link">
          <i class="fas fa-arrow-left"></i>
          Back to Ski Schools
        </router-link>
      </div>

      <!-- Ski School Container -->
      <SkiSchoolContainer
        :ski-school="skiSchool"
        :is-authenticated="isAuthenticated"
        :user-id="user?.id || null"
        @edit-school="editSkiSchool"
        @manage-coaches="manageCoaches"
      />

      <!-- Coach Management Modal -->
      <SkiCoachManagement
        :show="showCoachModal"
        :school="currentSchool"
        :user-id="user?.id || null"
        @close="closeCoachModal"
        @coach-added="onCoachAdded"
        @coach-removed="onCoachRemoved"
      />
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import SkiSchoolContainer from '@/components/SkiSchoolContainer.vue';
import SkiCoachManagement from '@/components/SkiCoachManagement.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import axios from 'axios';

export default {
  name: 'SkiSchoolDetail',
  components: {
    SkiSchoolContainer,
    SkiCoachManagement,
    LoadingSpinner
  },
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      skiSchool: null,
      loading: true,
      error: null,
      isAuthenticated: false,
      user: {},
      showCoachModal: false,
      currentSchool: null
    }
  },
  async created() {
    await this.checkAuthentication();
    await this.fetchSkiSchool();
  },
  methods: {
    async checkAuthentication() {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/profile/`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.user = response.data;
          this.isAuthenticated = true;
        } catch (error) {
          console.error("Error fetching user data:", error);
          this.isAuthenticated = false;
        }
      } else {
        this.isAuthenticated = false;
      }
    },

    async fetchSkiSchool() {
      try {
        this.loading = true;
        this.error = null;

        const response = await axios.get(`${API_BASE_URL}/profiles/ski-schools/${this.slug}/`);
        
        if (response.data) {
          this.skiSchool = response.data;
        } else {
          this.error = 'Ski school not found.';
        }
      } catch (error) {
        console.error('Error fetching ski school:', error);
        
        if (error.response && error.response.status === 404) {
          this.error = 'Ski school not found. It may have been removed or the URL is incorrect.';
        } else {
          this.error = 'Failed to load ski school. Please try again later.';
        }
      } finally {
        this.loading = false;
      }
    },

    editSkiSchool(school) {
      this.$router.push(`/ski-school/${school.slug}/edit`);
    },

    manageCoaches(school) {
      this.showCoachModal = true;
      this.currentSchool = school;
    },

    closeCoachModal() {
      this.showCoachModal = false;
      this.currentSchool = null;
    },

    onCoachAdded(updatedSchool) {
      this.skiSchool = updatedSchool;
      this.currentSchool = updatedSchool;
    },

    onCoachRemoved(updatedSchool) {
      this.skiSchool = updatedSchool;
      this.currentSchool = updatedSchool;
    }
  }
}
</script>

<style scoped>
.ski-school-detail-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.navigation-header {
  margin-bottom: 20px;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #1da1f2;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.back-link:hover {
  color: #1a91da;
}

.back-link i {
  font-size: 14px;
}

.error-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.error-content {
  text-align: center;
  color: #8899a6;
  padding: 40px;
}

.error-content i {
  font-size: 3em;
  color: #ff6b6b;
  margin-bottom: 20px;
}

.error-content h3 {
  color: #d9d9d9;
  margin-bottom: 10px;
}

.error-content p {
  margin-bottom: 20px;
  line-height: 1.5;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #1da1f2;
  color: #ffffff;
  text-decoration: none;
  padding: 10px 20px;
  border-radius: 20px;
  font-weight: 500;
  transition: background-color 0.3s ease;
}

.back-button:hover {
  background-color: #1a91da;
}

.ski-school-detail {
  /* The SkiSchoolContainer already has its own styling */
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .ski-school-detail-page {
    padding: 10px;
  }
}

@media (max-width: 600px) {
  .ski-school-detail-page {
    padding: 10px 4px 56px 4px;
  }
}
</style> 